"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReceiptManagerPublicationV1 = exports.ReceiptManagerDocument = exports.ReceiptManagerLinkToken = void 0;
exports.canonicalReceiptManagerPublication = canonicalReceiptManagerPublication;
// RMG publication v1 consumer contract, synchronized with receipt_manager [RL-582].
const zod_1 = require("zod");
// 128 random bits encoded in 26 Crockford digits: the two high padding bits are zero.
exports.ReceiptManagerLinkToken = zod_1.z.string().regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/);
const text = zod_1.z.string().max(16384);
const identity = zod_1.z.string().min(1).max(100);
const minorUnits = zod_1.z.string().regex(/^(0|[1-9][0-9]{0,29})$/);
const date = zod_1.z.string().datetime({ offset: true });
const lines = zod_1.z.array(text).max(1000);
const header = zod_1.z
    .object({
    type: zod_1.z.literal('header'),
    retailerName: text.optional(),
    logoRef: text.optional(),
    lines,
})
    .strict();
const item = zod_1.z
    .object({
    type: zod_1.z.literal('item_line'),
    name: text,
    quantityFormatted: text,
    unitPriceFormatted: text.optional(),
    lineTotalFormatted: text,
    sku: text.optional(),
    vatRateFormatted: text.optional(),
    discountFormatted: text.optional(),
})
    .strict();
const totals = zod_1.z
    .object({
    type: zod_1.z.literal('totals_block'),
    subtotalFormatted: text,
    discountFormatted: text.optional(),
    vatBreakdown: zod_1.z
        .array(zod_1.z
        .object({
        rateFormatted: text,
        baseFormatted: text,
        vatFormatted: text,
        groupCode: text.optional(),
        exciseFormatted: text.optional(),
    })
        .strict())
        .max(100)
        .optional(),
    totalFormatted: text,
})
    .strict();
const fiscalElement = zod_1.z
    .object({
    type: zod_1.z.literal('fiscal_block'),
    receiptNumber: text,
    fiscalNumber: text,
    timestampFormatted: text,
    signature: text.optional(),
    qrPayload: text.optional(),
    registrarId: text.optional(),
    testDocument: zod_1.z.boolean().optional(),
    testDocumentMarking: text.optional(),
})
    .passthrough(); // Fiscal integrations carry vendor extensions: preserve rather than silently strip them.
const footer = zod_1.z
    .object({
    type: zod_1.z.literal('footer'),
    retailerFooterText: text.optional(),
    lines,
    barcode: text.optional(),
})
    .strict();
const legal = zod_1.z
    .object({
    type: zod_1.z.literal('legal_text'),
    text: text.min(1),
    language: zod_1.z.string().regex(/^[a-z]{2,3}$/),
    sourceBlockId: text.optional(),
})
    .strict();
// Existing till register documents contain preformatted printer text, not HTML.
const register = zod_1.z.object({ type: zod_1.z.literal('register_document'), text }).passthrough();
exports.ReceiptManagerDocument = zod_1.z
    .object({
    receiptType: zod_1.z.literal('sale'),
    locale: zod_1.z.string().regex(/^[a-z]{2,3}$/),
    countryPackId: zod_1.z.string().max(100),
    packVersion: zod_1.z.string().max(100),
    generatedAt: date,
    elements: zod_1.z
        .array(zod_1.z.discriminatedUnion('type', [
        header,
        item,
        totals,
        fiscalElement,
        footer,
        legal,
        register,
    ]))
        .min(1)
        .max(2000),
})
    .strict();
// Extensions are JSON, not arbitrary JavaScript. JSON.parse can produce Infinity for 1e400.
// Bound traversal before canonicalization so pathological nesting cannot exhaust the stack.
function isFiniteJson(value) {
    const stack = [{ value, depth: 0 }];
    const seen = new Set();
    let nodes = 0;
    while (stack.length) {
        const entry = stack.pop();
        if (++nodes > 100000 || entry.depth > 64)
            return false;
        const current = entry.value;
        if (current === null || typeof current === 'string' || typeof current === 'boolean')
            continue;
        if (typeof current === 'number') {
            if (!Number.isFinite(current))
                return false;
            continue;
        }
        if (typeof current !== 'object' || seen.has(current))
            return false;
        if (!Array.isArray(current) &&
            Object.getPrototypeOf(current) !== Object.prototype &&
            Object.getPrototypeOf(current) !== null)
            return false;
        seen.add(current);
        for (const key of Object.keys(current)) {
            if (['__proto__', 'constructor', 'prototype'].includes(key))
                return false;
            stack.push({ value: current[key], depth: entry.depth + 1 });
        }
    }
    return true;
}
exports.ReceiptManagerPublicationV1 = zod_1.z
    .preprocess((input, ctx) => {
    // Validate raw input: Zod strips __proto__ keys before object refinements can see them.
    if (!isFiniteJson(input))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: 'Publication must be finite JSON, without prototype keys, with at most 64 levels and 100000 values',
        });
    return input;
}, zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(1),
    receiptLinkToken: exports.ReceiptManagerLinkToken,
    tenant: zod_1.z.object({ id: zod_1.z.string().uuid(), slug: identity }).strict(),
    store: zod_1.z.object({ id: identity, code: identity }).strict(),
    terminal: zod_1.z.object({ id: identity, code: identity }).strict(),
    receipt: zod_1.z
        .object({
        clientUuid: identity,
        type: zod_1.z.literal('sale'),
        finalisedAt: date,
        currency: zod_1.z.string().regex(/^[A-Z]{3}$/),
        totalMinor: minorUnits,
        payments: zod_1.z
            .array(zod_1.z.object({ kind: identity, amountMinor: minorUnits }).strict())
            .max(20),
    })
        .strict(),
    fiscal: zod_1.z
        .object({
        status: zod_1.z.enum(['issued', 'non_fiscal']).optional(),
        receiptNumber: text,
        fiscalNumber: text,
        registrarId: text,
        timestampFormatted: text,
        qrPayload: text.optional(),
        testDocument: zod_1.z.boolean(),
    })
        .passthrough(),
    receiptDocument: exports.ReceiptManagerDocument,
})
    .strict())
    .superRefine((value, ctx) => {
    // Zod refinements may run on a dirty parse after a regex failure. Never throw on invalid input.
    if (!minorUnits.safeParse(value.receipt.totalMinor).success ||
        value.receipt.payments.some((leg) => !minorUnits.safeParse(leg.amountMinor).success))
        return;
    const paid = value.receipt.payments.reduce((sum, leg) => sum + BigInt(leg.amountMinor), 0n);
    if (paid !== BigInt(value.receipt.totalMinor))
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['receipt', 'payments'],
            message: 'Payment amounts must equal totalMinor; amounts are net tender, excluding change',
        });
    if (value.fiscal.status === 'non_fiscal' &&
        (Object.keys(value.fiscal).some((key) => ![
            'status',
            'receiptNumber',
            'fiscalNumber',
            'registrarId',
            'timestampFormatted',
            'qrPayload',
            'testDocument',
        ].includes(key)) ||
            value.fiscal.testDocument ||
            [
                value.fiscal.receiptNumber,
                value.fiscal.fiscalNumber,
                value.fiscal.registrarId,
                value.fiscal.timestampFormatted,
                value.fiscal.qrPayload ?? '',
            ].some(Boolean) ||
            value.receiptDocument.elements.some((e) => e.type === 'fiscal_block' || e.type === 'register_document'))) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['fiscal'],
            message: 'Non-fiscal receipts cannot claim fiscal identity, test mode, or register documents',
        });
    }
    const testFlags = value.receiptDocument.elements.filter((e) => e.type === 'fiscal_block');
    for (const element of testFlags) {
        if (element.type === 'fiscal_block' &&
            typeof element.testDocument === 'boolean' &&
            element.testDocument !== value.fiscal.testDocument) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['fiscal', 'testDocument'],
                message: 'Envelope and document test flags disagree',
            });
        }
    }
});
// Object-key order is irrelevant; array order and all formatted receipt strings remain significant.
function canonicalReceiptManagerPublication(input) {
    const value = exports.ReceiptManagerPublicationV1.parse(input);
    function sorted(inputValue) {
        if (Array.isArray(inputValue))
            return inputValue.map(sorted);
        if (inputValue && typeof inputValue === 'object') {
            return Object.fromEntries(Object.entries(inputValue)
                .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
                .map(([key, v]) => [key, sorted(v)]));
        }
        return inputValue;
    }
    return JSON.stringify(sorted(value));
}
//# sourceMappingURL=receipt-manager-publication.js.map